/**
 * Flight data QC's "Actioned" column (decision 751, 27 Oct 2026; spec docs/specs/geosoft-qc.md §12): whether every
 * line a QC run failed or flagged to check has been dealt with. One rule for Teslyn Field's run list, its menu count
 * and Project overview line, and Teslyn Cloud's Flights page.
 *
 * - A line is actioned by a saved "Acknowledge & accepted" (its newest `qc_acceptance` row is an acknowledgement; an
 *   undo makes it pending again), or by a saved Line review decision with a reason or a note: accepted, part accepted
 *   or rejected, a part acceptance or a reject only once its patch or reflight is planned (a later version of the line
 *   in the plan, as `followUpPlan` makes it). After an undo only a review saved later counts. Whether the line was
 *   flown in full, or its km accepted, does not matter; words typed and not saved are not data.
 * - Only the newest live run of each flight counts (by `started_at`); older runs of the flight are "Superseded".
 * - A run with nothing to action (every line passed, or the run failed) shows "—".
 */
import type { IsoDateTime, LineStatus, PlannedLine, Uuid } from './model.js';
import type { QcLineResult, QcRun } from './geosoft-qc.js';
import { parseLineNumber } from './lines.js';
import { QC_ACCEPTED_WORDS, qcAcceptanceState, qcTimeWords, type QcAcceptance } from './qc-acceptance.js';

export type QcActionedState = 'pending' | 'complete' | 'nothing' | 'superseded';

/** How a line was actioned, by whom (null: not known here) and when. */
export interface QcLineAction {
  readonly how: 'acknowledged' | 'reviewed';
  readonly by: string | null;
  readonly at: IsoDateTime | null;
}

export interface QcRunActioned {
  readonly run_id: Uuid;
  readonly state: QcActionedState;
  /** The lines to action (fail or check). */
  readonly total: number;
  /** Their names still waiting, in the run's order. */
  readonly waiting: readonly string[];
  /** The line actioned last (Complete's hover). */
  readonly last: (QcLineAction & { readonly line_name: string }) | null;
  /** The chip's words: "Pending 3 of 9", "Complete", "—", "Superseded". */
  readonly words: string;
  /** What the chip's hover says. */
  readonly hover: string;
}

export type QcActionedRunInput = Pick<QcRun, 'id' | 'flight_id' | 'status' | 'started_at' | 'deleted_at'> &
  Partial<Pick<QcRun, 'source_path'>>;

/**
 * Which runs supersede each other: the same flight's, on the same block's database (decision 868: a flight over
 * several blocks has a database in each block's own Geosoft folder, each checked on its own). Runs without their
 * database's path are the flight's.
 */
function supersedeKey(r: QcActionedRunInput): string {
  const p = r.source_path ?? '';
  const folder = p.replace(/[\\/][^\\/]*$/, '').toLowerCase();
  return `${r.flight_id}|${p === '' ? '' : folder}`;
}
export type QcActionedLineInput = Pick<
  QcLineResult,
  'id' | 'run_id' | 'line_status_id' | 'line_name' | 'verdict' | 'deleted_at'
>;
export type QcActionedStatusInput = Pick<
  LineStatus,
  'id' | 'acceptance' | 'reason' | 'note' | 'reviewed_by' | 'reviewed_at' | 'line_number' | 'deleted_at'
>;

export interface QcActionedInput {
  readonly runs: readonly QcActionedRunInput[];
  /** The runs' line results (other runs' are ignored). */
  readonly lines: readonly QcActionedLineInput[];
  readonly acceptances: readonly QcAcceptance[];
  readonly statuses: readonly QcActionedStatusInput[];
  /** The plan, to tell a reject or part acceptance whose reflight or patch is planned. */
  readonly plan: readonly Pick<PlannedLine, 'number' | 'deleted_at'>[];
  /** A reviewer's name by user id; null when not known (the hover then names no one). */
  readonly nameOf?: (userId: Uuid) => string | null;
  /** UTC offset in minutes for the hover's times (the computer's by default). */
  readonly offsetMin?: number;
}

const ms = (t: string | null | undefined): number => (t ? Date.parse(t) : Number.NaN);

/** Whether a part acceptance or reject has its patch or reflight planned: a later version of the line in the plan. */
function followUpPlanned(lineNumber: number, plan: QcActionedInput['plan']): boolean {
  const { base, production } = parseLineNumber(lineNumber);
  if (!production) return true;
  const live = plan.filter((p) => p.deleted_at === null);
  // a line with no original in the plan gets no follow-up (`followUpPlan`): nothing to wait for
  if (!live.some((p) => p.number === base)) return true;
  return live.some((p) => p.number > lineNumber && parseLineNumber(p.number).base === base);
}

/** How a QC line was actioned, or null while it waits. */
export function qcLineAction(
  line: QcActionedLineInput,
  input: Pick<QcActionedInput, 'acceptances' | 'statuses' | 'plan' | 'nameOf'>,
): QcLineAction | null {
  const ack = qcAcceptanceState(input.acceptances, line.id);
  if (ack.accepted && ack.current)
    return { how: 'acknowledged', by: ack.current.by_name.trim() || null, at: ack.current.acted_at };
  if (line.line_status_id === null) return null;
  const s = input.statuses.find((x) => x.id === line.line_status_id && x.deleted_at === null);
  if (!s || s.acceptance === 'pending') return null;
  if (s.reason.trim() === '' && s.note.trim() === '') return null;
  // decision 751: an undo makes the line pending again; only a review saved after it counts
  if (ack.current?.action === 'undone' && !(ms(s.reviewed_at) > ms(ack.current.acted_at))) return null;
  if (
    (s.acceptance === 'rejected' || s.acceptance === 'partial') &&
    !followUpPlanned(s.line_number, input.plan)
  )
    return null;
  return {
    how: 'reviewed',
    by: s.reviewed_by ? (input.nameOf?.(s.reviewed_by) ?? null) : null,
    at: s.reviewed_at,
  };
}

const HOW: Readonly<Record<QcLineAction['how'], string>> = {
  acknowledged: QC_ACCEPTED_WORDS.toLowerCase(),
  reviewed: 'reviewed in Line review',
};

/** Whether `a` was actioned after `b` (a time beats none). */
const later = (a: QcLineAction, b: QcLineAction): boolean =>
  b.at === null ? a.at !== null : a.at !== null && ms(a.at) > ms(b.at);

/** The Actioned state of every live run, by run id. */
export function qcActioned(input: QcActionedInput): Map<Uuid, QcRunActioned> {
  const runs = input.runs.filter((r) => r.deleted_at === null);
  const newest = new Map<string, QcActionedRunInput>();
  for (const r of runs) {
    if (r.flight_id === null) continue;
    const n = newest.get(supersedeKey(r));
    if (!n || ms(r.started_at) > ms(n.started_at)) newest.set(supersedeKey(r), r);
  }
  const out = new Map<Uuid, QcRunActioned>();
  for (const r of runs) {
    const none = { run_id: r.id, total: 0, waiting: [] as string[], last: null };
    const counts = r.flight_id === null ? r : (newest.get(supersedeKey(r)) ?? r);
    if (counts.id !== r.id) {
      out.set(r.id, {
        ...none,
        state: 'superseded',
        words: 'Superseded',
        hover: `A newer QC run of this flight counts (started ${qcTimeWords(counts.started_at, input.offsetMin)}).`,
      });
      continue;
    }
    if (r.status === 'failed') {
      out.set(r.id, { ...none, state: 'nothing', words: '—', hover: 'Nothing to action: the run failed.' });
      continue;
    }
    const toAction = input.lines.filter(
      (l) => l.run_id === r.id && l.deleted_at === null && (l.verdict === 'fail' || l.verdict === 'warn'),
    );
    if (toAction.length === 0) {
      out.set(r.id, {
        ...none,
        state: 'nothing',
        words: '—',
        hover: 'Nothing to action: every line passed.',
      });
      continue;
    }
    const waiting: string[] = [];
    let last: (QcLineAction & { line_name: string }) | null = null;
    for (const l of toAction) {
      const a = qcLineAction(l, input);
      if (!a) waiting.push(l.line_name);
      else if (!last || later(a, last)) last = { ...a, line_name: l.line_name };
    }
    const total = toAction.length;
    if (waiting.length > 0) {
      out.set(r.id, {
        run_id: r.id,
        state: 'pending',
        total,
        waiting,
        last,
        words: `Pending ${waiting.length} of ${total}`,
        hover: `Still to action: ${waiting.join(', ')}.`,
      });
      continue;
    }
    const who = last?.by ? ` by ${last.by}` : '';
    const at = last?.at ? `, ${qcTimeWords(last.at, input.offsetMin)}` : '';
    out.set(r.id, {
      run_id: r.id,
      state: 'complete',
      total,
      waiting,
      last,
      words: 'Complete',
      hover: last
        ? `Complete: the last line actioned was ${last.line_name}, ${HOW[last.how]}${who}${at}.`
        : 'Complete.',
    });
  }
  return out;
}

/** The flights whose QC actions are pending (their newest run): the menu's count and the Project overview's line. */
export function qcFlightsPending(actioned: ReadonlyMap<Uuid, QcRunActioned>): number {
  return [...actioned.values()].filter((a) => a.state === 'pending').length;
}

const RANK: Readonly<Record<QcActionedState, number>> = {
  pending: 0,
  complete: 1,
  nothing: 2,
  superseded: 3,
};

/** The column's sort value: pending first (most lines waiting first), then complete, nothing to action, superseded. */
export function qcActionedSortValue(a: Pick<QcRunActioned, 'state' | 'waiting'>): [number, number] {
  return [RANK[a.state], -a.waiting.length];
}
